import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, lastValueFrom, Subscription } from 'rxjs';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { LoadOptions } from 'devextreme/data';
import DataSource from 'devextreme/data/data_source';
import CustomStore from 'devextreme/data/custom_store';
import { DxDataGridComponent } from 'devextreme-angular';

import { LocatorLibraryData } from 'src/app/locator/models/locator-library-data';
import {
  ListSortOptions,
  SortOptions,
  SearchAndSortValues,
  LocatorListSortOptions,
  applyMatchLevelSort
} from 'src/app/shared/utils/list-utils';
import * as fromLocator from 'src/app/locator-store';
import * as fromGazetteerStore from 'src/app/shared/atlas-gazetteer/store';
import {
  getLocatorLibraryDataSucceeded,
  autoLoadReferenceLibraryAtttempt
} from 'src/app/locator-store/actions/locator-library.actions';
import { LocatorService } from 'src/app/locator/services/locator.service';
import { DataGridService } from '../../services/datagrid.service';
import {
  clearMapAndGazetterForMultiSelect,
  deselectAllLocations,
  libraryLocationErrorOccurred
} from 'src/app/locator-store/actions/locator-location.actions';
import { SelectionChangedEvent } from 'devextreme/ui/data_grid';
import * as fromLocatorUIStore from 'src/app/core/store';
import { LayoutService } from 'src/app/shared/atlas-layout/services/layout.service';

@Component({
  selector: 'atlas-library-locations-list',
  styleUrls: ['./locator-library-locations-list.component.less'],
  templateUrl: './locator-library-locations-list.component.html',
  standalone: false
})
export class LocatorLibraryLocationsListComponent implements OnInit {
  private _libraryId: number | null;
  private subscription: Subscription = new Subscription();
  private selectedSortOption: ListSortOptions | LocatorListSortOptions =
    'NameAsc';

  @ViewChild(DxDataGridComponent) dataGrid: DxDataGridComponent;

  @Input()
  template: TemplateRef<any>;

  @Input() selectedLocationId: number | null;

  @Input()
  set libraryId(newValue: number | null) {
    this._libraryId = newValue;
    this.resetForm();
    this.resetGridlist();
  }
  get libraryId() {
    return this._libraryId;
  }

  @Output()
  locationsClickSelected = new EventEmitter<LocatorLibraryData[]>();

  sortOptions: SortOptions[] = [
    {
      value: 'MatchLevelHighLow',
      display: $localize`:@@matchLevelHighLow:Match Level: High to Low`
    },
    {
      value: 'MatchLevelLowHigh',
      display: $localize`:@@matchLevelLowHigh:Match Level: Low to High`
    },
    {
      value: 'NameAsc',
      display: $localize`:@@nameAsc:Name: A-Z`
    },
    {
      value: 'NameDesc',
      display: $localize`:@@nameDesc:Name: Z-A`
    },
    {
      value: 'CreatedNewestOldest',
      display: $localize`:@@createdNewestOldest:Date Created: Newest to Oldest`
    },
    {
      value: 'CreatedOldestNewest',
      display: $localize`:@@createdOldestNewest:Date Created: Oldest to Newest`
    },
    {
      value: 'ModifiedLatestOldest',
      display: $localize`:@@modifiedLatestOldest:Date Modified: Latest to Oldest`
    },
    {
      value: 'ModifiedOldestLatest',
      display: $localize`:@@modifiedOldestLatest:Date Modified: Oldest to Latest`
    },
    {
      value: 'CatchmentsHighLow',
      display: $localize`:@@catchmentsHighLow:Catchments: High to Low`
    },
    {
      value: 'CatchmentsLowHigh',
      display: $localize`:@@catchmentsLowHigh:Catchments: Low to High`
    }
  ];
  defaultSort: any[] = [{ selector: 'name', desc: false }];
  selectionMode: 'single' | 'multiple' = 'single';
  isSelected = false;
  allSelected = false;
  searchForm: UntypedFormGroup;
  countMessage$ = new BehaviorSubject<string>('');
  totalCount: number | undefined = 0;
  filteredCount: number;
  columnWidth: number = 228;
  showStatsOverlay = false;
  showSummaryStatsReport: boolean;

  showSummaryStatsReport$ = this.locatorUIStore$.select(
    fromLocatorUIStore.showSummaryStatsReport
  );

  customDataSource = new DataSource({
    store: new CustomStore({
      key: 'id',
      load: async (loadOptions: LoadOptions) => {
        // Default sort configuration
        if (loadOptions.sort == null) {
          loadOptions.sort = this.defaultSort;
        }

        this.allSelected
          ? (loadOptions.userData = { selectAll: true })
          : (loadOptions.userData = {});

        // Load one page for the grid, or all matching pages for Select All.
        const result = await this.loadLocations(loadOptions);

        this.totalCount = result.allRecordsCount;
        this.filteredCount = result.locatorLoadResult.totalCount;

        if (this.totalCount === 1) {
          this.selectionMode = 'single';
          this.isSelected = false;
          this.columnWidth = 228;
          this.allSelected = false;
        }

        this.sortGeocodeMatchLevel(loadOptions, result.locatorLoadResult.data);

        this.customDataSource.on('changed', () => {
          const msg = this.searchForm.value.filter
            ? $localize`:@@countMessage:Showing ${this.filteredCount} of ${this.totalCount} total`
            : $localize`:@@countMessageTotal:Showing ${this.totalCount} total`;

          this.countMessage$.next(msg);
        });

        this.store$.dispatch(
          getLocatorLibraryDataSucceeded({
            locations: result.locatorLoadResult.data
          })
        );

        this.store$.dispatch(
          autoLoadReferenceLibraryAtttempt({
            numLocations: result.allRecordsCount,
            libraryId: this.libraryId!
          })
        );

        return result.locatorLoadResult;
      }
    })
  });

  constructor(
    private fb: UntypedFormBuilder,
    private store$: Store<fromLocator.State>,
    private locatorDataService: LocatorService,
    private dataGridService: DataGridService,
    private gazetteerStore$: Store<fromGazetteerStore.State>,
    private locatorUIStore$: Store<fromLocatorUIStore.State>,
    public layoutService: LayoutService
  ) {}

  ngOnInit(): void {
    const initialValue: SearchAndSortValues = {
      filter: '',
      sort: 'NameAsc'
    } as SearchAndSortValues;
    this.searchForm = this.createForm(initialValue);

    this.subscription.add(
      this.showSummaryStatsReport$.subscribe(
        (showSummaryStatsReport) =>
          (this.showSummaryStatsReport = showSummaryStatsReport)
      )
    );
  }

  ngAfterViewInit() {
    this.dataGridService.setDataGrid(this.dataGrid);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  createForm(initialLibraryValue: SearchAndSortValues) {
    return this.fb.group({
      filter: [initialLibraryValue.filter],
      sort: [initialLibraryValue.sort]
    });
  }

  onSortChanged(event: any) {
    this.selectedSortOption = event.value;
    this.dataGrid.instance.clearSorting();

    switch (this.selectedSortOption) {
      case 'MatchLevelHighLow':
        this.dataGrid.instance.columnOption('matchLevel', {
          sortOrder: 'asc'
        });
        break;
      case 'MatchLevelLowHigh':
        this.dataGrid.instance.columnOption('matchLevel', {
          sortOrder: 'desc'
        });
        break;
      case 'NameAsc':
        this.dataGrid.instance.columnOption('name', { sortOrder: 'asc' });
        break;
      case 'NameDesc':
        this.dataGrid.instance.columnOption('name', { sortOrder: 'desc' });
        break;
      case 'CreatedNewestOldest':
        this.dataGrid.instance.columnOption('createDt', { sortOrder: 'desc' });
        break;
      case 'CreatedOldestNewest':
        this.dataGrid.instance.columnOption('createDt', { sortOrder: 'asc' });
        break;
      case 'ModifiedLatestOldest':
        this.dataGrid.instance.columnOption('editedDt', { sortOrder: 'desc' });
        break;
      case 'ModifiedOldestLatest':
        this.dataGrid.instance.columnOption('editedDt', { sortOrder: 'asc' });
        break;
      case 'CatchmentsHighLow':
        this.dataGrid.instance.columnOption(
          this.allSelected ? 'shapeCount' : 'shapes.length',
          { sortOrder: 'desc' }
        );
        break;
      case 'CatchmentsLowHigh':
        this.dataGrid.instance.columnOption(
          this.allSelected ? 'shapeCount' : 'shapes.length',
          { sortOrder: 'asc' }
        );
        break;

      default:
        throw new Error($localize`:@@sortOptionInvalid:Sort Option invalid`);
    }
  }

  onFilterChanged(event: Event) {
    let filterValue = (event.target as HTMLInputElement).value;
    this.dataGrid.instance.searchByText(filterValue);

    this.dataGrid?.instance
      .getDataSource()
      .store()
      .load({
        take: undefined,
        filter: this.dataGrid?.instance.getCombinedFilter()
      })
      .then((result: any) => {
        const selectedRows = this.dataGrid.instance.getSelectedRowKeys();
        this.dataGrid.instance.selectRows(
          (result?.data as any[])
            .filter((item) => selectedRows.includes(item.id))
            .map((item) => item.id),
          false
        );
      });
  }

  onSelectionChanged(event: SelectionChangedEvent) {
    // Check whether all matching locations are selected, including those on other pages.
    this.allSelected =
      event.selectedRowKeys.length > 0 &&
      event.selectedRowKeys.length ===
        (this.filteredCount ?? this.totalCount) &&
      this.totalCount !== 1;

    // Send all selected locations for batch catchments.
    // Skip the reload to avoid another request for the current 100-row page.
    if (event.selectedRowKeys.length > 1) {
      this.locationsClickSelected.emit(event.selectedRowsData);
      this.clearMapAndGazetteerForMultiSelect();
      return;
    }

    this.dataGrid?.instance
      .getDataSource()
      .reload()
      .then(async (dataSource: LocatorLibraryData[]) => {
        if (event.selectedRowKeys.length === 1) {
          const selectedLocationId = event.selectedRowKeys[0];
          // The last selected location may be on another page or have no shape details loaded.
          // Fetch its details if needed so existing shapes can still be checked.
          const selectedItem =
            dataSource.find(
              (location) =>
                location.id === selectedLocationId &&
                Array.isArray(location.shapes)
            ) ??
            event.selectedRowsData.find(
              (location) =>
                location.id === selectedLocationId &&
                Array.isArray(location.shapes)
            ) ??
            (await lastValueFrom(
              this.locatorDataService.getLocatorDataById(selectedLocationId)
            ));
          // Ignore results if the selection changed while loading.
          const selectedRowKeys = this.dataGrid.instance.getSelectedRowKeys();
          if (
            !selectedItem ||
            selectedItem.id !== selectedLocationId ||
            selectedRowKeys.length !== 1 ||
            selectedRowKeys[0] !== selectedLocationId
          ) {
            return;
          }
          this.locationsClickSelected.emit([selectedItem]);
        }

        if (event.selectedRowKeys.length === 0) {
          this.locationsClickSelected.emit(event.selectedRowsData);
          this.dataGrid?.instance.option('focusedRowKey', null);
          this.dataGrid?.instance.deselectAll().then(async () => {
            await this.reloadGrid();
          });
          this.dispatchDeselectAll();
        }
      })
      .catch(() => {
        this.store$.dispatch(
          libraryLocationErrorOccurred({
            errorOn: 'Error Library location',
            error: 'Retrieving library location has failed.'
          })
        );
      });
  }

  toggleSelection(event: MatCheckboxChange) {
    if (!event.checked) {
      this.deselectedSelectedItems();
    }
    this.isSelected = event.checked;
    this.allSelected = false;
    this.selectionMode = event.checked ? 'multiple' : 'single';
    this.columnWidth = this.isSelected ? 190 : 228;
    this.reloadGrid();
  }

  deselectedSelectedItems() {
    this.resetGridlist();
    this.dispatchDeselectAll();
  }

  async selectAll(event: MatCheckboxChange) {
    this.allSelected = event.checked;
    if (event.checked) {
      this.selectAllFromFiltered();
    } else {
      this.dataGrid?.instance.deselectAll();
    }
  }

  selectAllFromFiltered() {
    const filter = this.dataGrid?.instance.getCombinedFilter();
    if (filter) {
      // Select all rows that satisfy the current filter, not just the visible page
      this.dataGrid?.instance
        .getDataSource()
        .store()
        .load({ filter })
        .then((result: any) => {
          const data = Array.isArray(result) ? result : (result?.data ?? []);
          const idsToSelect = data.map((item: any) => item.id);
          this.dataGrid?.instance.selectRows(idsToSelect, false);
        });
    } else {
      this.dataGrid?.instance.selectAll();
    }
  }

  getShapeCountDataField(): string {
    let result = this.allSelected ? 'shapeCount' : 'shapes.length';
    return result;
  }

  private clearMapAndGazetteerForMultiSelect() {
    this.gazetteerStore$.dispatch(clearMapAndGazetterForMultiSelect());
  }

  private dispatchDeselectAll() {
    this.store$.dispatch(deselectAllLocations());
  }

  private async resetGridlist() {
    if (this.dataGrid) {
      this.dataGrid.instance.clearFilter();
      this.dataGrid.instance.clearSelection();
      this.dataGrid?.instance.option('focusedRowKey', null);
      this.selectionMode = 'single';
      this.isSelected = false;
      this.allSelected = false;
      await this.reloadGrid();
    }
  }

  private resetForm() {
    this.searchForm?.patchValue({
      filter: '',
      sort: 'NameAsc'
    });
  }

  private async reloadGrid() {
    await this.dataGrid?.instance.getDataSource().reload();
  }

  private async loadLocations(loadOptions: LoadOptions) {
    const libraryId = this.libraryId!;
    const loadPage = (options: LoadOptions) =>
      lastValueFrom(
        this.locatorDataService.getLocatorDataByLibrarySearch(
          libraryId,
          options
        )
      ) as Promise<{
        allRecordsCount: number;
        locatorLoadResult: { data: LocatorLibraryData[]; totalCount: number };
      }>;

    // Keep normal grid loading unchanged: fetch only the requested page.
    if (loadOptions.take) {
      return loadPage(loadOptions);
    }

    // Without take, all matching locations are needed for selection or filtering.
    // Load them in separate requests to avoid one oversized response.
    const pageOptions = {
      ...loadOptions,
      // Sort by unique ID so duplicate names do not cause rows to be missed between pages.
      sort: [{ selector: 'id', desc: false }],
      // Ask for IDs and shape counts instead of full shape details to keep responses small.
      userData: { selectAll: true },
      take: 50000
    };
    const result = await loadPage({ ...pageOptions, skip: 0 });
    const data = [...result.locatorLoadResult.data];

    // Keep loading until every location matching the filter has been collected.
    while (data.length < result.locatorLoadResult.totalCount) {
      const page = await loadPage({ ...pageOptions, skip: data.length });
      if (page.locatorLoadResult.data.length === 0) {
        // Stop rather than run batch catchments with an incomplete selection.
        throw new Error('Location selection could not be fully loaded.');
      }
      data.push(...page.locatorLoadResult.data);
    }

    return {
      ...result,
      locatorLoadResult: { ...result.locatorLoadResult, data }
    };
  }

  private sortGeocodeMatchLevel(loadOptions: any, data: any[]) {
    if (loadOptions?.sort[0]?.selector?.includes('matchLevel')) {
      applyMatchLevelSort(data, !loadOptions.sort[0].desc);
    }
  }

  onStatsClick(): void {
    this.locatorUIStore$.dispatch(fromLocatorUIStore.showStatsOverlay());
  }
}
